/**
 * Phonetool helpers — canonical URLs for employee profile pages and
 * badge photos. Single point of change if the corp endpoints ever
 * move again (the badge-photo endpoint was migrated once in June
 * 2026 already; see `src/utils/badgePhoto.ts`).
 *
 * V2 code should import from `shared/domain` — do NOT inline the
 * `phonetool.amazon.com` or `badgephotos.corp.amazon.com` URLs at
 * call sites.
 */

const PHONETOOL_PROFILE_BASE_URL = 'https://phonetool.amazon.com/users/';

/**
 * Return a phonetool profile URL for the given login. The login is
 * URL-encoded so unusual aliases (containing punctuation, spaces, or
 * non-ASCII characters) don't produce malformed URLs.
 *
 * Returns `null` when `login` is empty or whitespace. Callers should
 * treat `null` as "don't render the link" — the badge photo endpoint
 * would return a silhouette for the empty string but there's no
 * useful profile page to navigate to, so the sensible fallback is to
 * suppress the link entirely.
 *
 * @param login canonical Amazon alias
 * @returns absolute URL to the phonetool profile page, or `null` when
 *          the alias is empty
 */
export function phonetoolProfileUrl(login: string): string | null {
  const trimmed = login.trim();
  if (!trimmed) return null;
  return `${PHONETOOL_PROFILE_BASE_URL}${encodeURIComponent(trimmed)}`;
}

/**
 * Re-export of the badge-photo URL helper so V2 code can reach both
 * phonetool primitives from `shared/domain` with one import. The
 * implementation stays in `src/utils/badgePhoto.ts` because V1 code
 * still consumes it directly.
 */
export { badgePhotoUrl as phonetoolBadgePhotoUrl } from 'src/utils/badgePhoto';
